import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAlert } from 'react-alert';
import moment from 'moment';
import Loader from '../Loader';
import CustomDataTable from '../Datatable/CustomDataTable';
import './subject.css';
import './RegisteredSubjectsPage.css';
import { getSavedExamSubjects, saveExamSubjects } from '../../actions/admin/examSubjectAction';
import { saveSelectedSubjects } from '../../actions/subjectActions';
import { confirmAlert } from 'react-confirm-alert';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { getUserSubscription, getUserSubscriptionList } from '../../actions/subscriptionActions';
import { isSubscriptionCurrentlyActive } from '../../utils/examSubscription';

const getExamTone = (slug = '', title = '') => {
    const value = `${slug} ${title}`.toLowerCase();
    if (value.includes('jamb')) return 'blue';
    if (value.includes('wassce') || value.includes('waec')) return 'green';
    if (value.includes('post-utme') || value.includes('post utme')) return 'orange';
    if (value.includes('bece') || value.includes('jsce')) return 'purple';
    return 'blue';
};

const getCategoryTone = (title = '') => {
    const value = title.toLowerCase();
    if (value.includes('art')) return 'arts';
    if (value.includes('sci')) return 'science';
    return 'default';
};

const getExamBucket = (slug = '', title = '') => {
    const value = `${slug} ${title}`.toLowerCase();
    if (value.includes('jamb')) return 'jamb';
    if (value.includes('wassce') || value.includes('waec')) return 'wassce';
    return 'other';
};

const matchesSearchQuery = (query, values = []) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return true;
    return values.some((value) => String(value || '').toLowerCase().includes(normalized));
};

const SubjectListCell = ({ item, rowKey, currSubjects, onCheck }) => {
    const [expanded, setExpanded] = useState(false);
    const subjects = Array.isArray(item?.subjects) ? item.subjects : [];
    const visibleCount = 3;
    const visibleSubjects = expanded ? subjects : subjects.slice(0, visibleCount);
    const hiddenCount = Math.max(subjects.length - visibleCount, 0);

    return (
        <div>
            <ul className="student-registered-subjects__subjects">
                {visibleSubjects.map((subject, index) => {
                    const subjId = subject?.subject?._id;
                    const itemCurr = currSubjects.find((entry) => entry.itemId === item._id);
                    const isSelectedTemp = itemCurr ? itemCurr.subjects.includes(subjId) : false;
                    const isRegistered = item?.subjectsProcessed?.includes(subjId);
                    const inputId = `subject-${subjId}-${rowKey}`;

                    return (
                        <li key={subjId || index} className="student-registered-subjects__subject">
                            {isRegistered ? (
                                <span className="student-registered-subjects__subject-check" aria-hidden="true">
                                    <i className="fas fa-check" />
                                </span>
                            ) : (
                                <>
                                    <input
                                        type="checkbox"
                                        className="student-registered-subjects__subject-input"
                                        id={inputId}
                                        onChange={onCheck}
                                        value={subjId}
                                        data-index={item?._id}
                                        data-exam={item?.exam?.slug}
                                        checked={isSelectedTemp}
                                    />
                                    <label
                                        htmlFor={inputId}
                                        className={`student-registered-subjects__subject-check student-registered-subjects__subject-check--selectable${
                                            isSelectedTemp ? ' is-selected' : ''
                                        }`}
                                    >
                                        <i className={`fas ${isSelectedTemp ? 'fa-check' : ''}`} aria-hidden="true" />
                                    </label>
                                </>
                            )}
                            <span className="student-registered-subjects__subject-name">
                                {subject?.subject?.title}
                            </span>
                        </li>
                    );
                })}
            </ul>
            {!expanded && hiddenCount > 0 ? (
                <button
                    type="button"
                    className="student-registered-subjects__more"
                    onClick={() => setExpanded(true)}
                >
                    +{hiddenCount} more
                </button>
            ) : null}
        </div>
    );
};

const SavedExamSubjects = ({ history, match }) => {
    const alert = useAlert();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { userSubscription, userSubscriptionList, sloading } = useSelector((state) => state.examSubscriptions);
    const { savedSubjects, csloading } = useSelector((state) => state.examSubjects);
    const { subject: savedRegisteredSubject, isSaved } = useSelector((state) => state.courseSubjects);

    const [currSubjects, setCurrSubjects] = useState([]);
    const [proceedSubjects, setProceedSubjects] = useState([]);
    const [registeredSubject, setRegisteredSubject] = useState([]);
    const [updateRequest, setUpdateRequest] = useState([]);
    const [maxSubjects, setMaxSubjects] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const userId = user?._id;
    const router = useHistory();

    const isSubscriptionActive = (subscription) => {
        if (!subscription) {
            return false;
        }
        if (subscription?.status === false) {
            return false;
        }
        if (!subscription?.expireAt) {
            return false;
        }
        return moment(subscription.expireAt).isSameOrAfter(moment());
    };

    const getEntityId = (entity) => {
        if (!entity) {
            return null;
        }
        if (typeof entity === 'string') {
            return entity;
        }
        if (typeof entity === 'object') {
            return entity._id || entity.id || null;
        }
        return null;
    };

    const persistExamContext = (item, subscriptionId = null, options = {}) => {
        const setOrRemove = (key, value) => {
            if (value !== undefined && value !== null && `${value}`.trim() !== "") {
                localStorage.setItem(key, value);
            } else {
                localStorage.removeItem(key);
            }
        };

        setOrRemove("examId", item?.exam?._id);
        setOrRemove("examSlug", item?.exam?.slug);
        setOrRemove("examName", item?.exam?.title);
        setOrRemove("examType", item?.exam?.examType);
        setOrRemove("examinationType", item?.exam?.examType);
        setOrRemove("categoryId", item?.category?._id);
        setOrRemove("courseId", item?.course?._id);
        setOrRemove("maximumSubjects", item?.exam?.maximumSubjects);

        const subjectIds = (item?.subjects || [])
            .map((subject) => subject?.subject?._id)
            .filter(Boolean);

        if (subjectIds.length > 0) {
            const serializedSubjects = subjectIds.join(',');
            localStorage.setItem("processedSubjects", serializedSubjects);
            localStorage.setItem("subjects", serializedSubjects);
        } else {
            localStorage.removeItem("processedSubjects");
            localStorage.removeItem("subjects");
        }

        const resolvedUniversityId = options?.universityId ?? getEntityId(item?.subscriptionPlan?.university) ?? null;
        setOrRemove("universityId", resolvedUniversityId);
        setOrRemove("userSubscription", subscriptionId);
    };

    const promptSubscriptionRenewal = (item, subscriptionDetails) => {
        const examTitle = item?.exam?.title || 'this exam';
        const expiryText = subscriptionDetails?.expireAt
            ? moment(subscriptionDetails.expireAt).format('MMMM Do, YYYY')
            : null;

        confirmAlert({
            title: 'Subscription Required',
            message: (
                <div>
                    {subscriptionDetails ? (
                        <>
                            <p>
                                Your <strong>subscription</strong> for <strong>{examTitle}</strong> expired
                                {expiryText ? <> on <strong>{expiryText}</strong></> : null}.
                            </p>
                            {subscriptionDetails?.planName ? (
                                <p>Current plan: <strong>{subscriptionDetails.planName}</strong>.</p>
                            ) : null}
                            <p>Please renew your subscription to continue.</p>
                        </>
                    ) : (
                        <>
                            <p>You do not have an active subscription for <strong>{examTitle}</strong>.</p>
                            <p>Please renew your subscription to continue.</p>
                        </>
                    )}
                </div>
            ),
            buttons: [
                {
                    label: 'Renew Now',
                    onClick: () => {
                        history.push('/subscription-plans');
                    },
                },
                {
                    label: 'Cancel',
                    onClick: () => { },
                },
            ],
        });
    };

    useEffect(() => {
        if (userId) {
            dispatch(getSavedExamSubjects(userId));
            dispatch(getUserSubscriptionList(userId));
        }
    }, [dispatch, userId]);


    useEffect(() => {
        if (savedSubjects && savedSubjects.length > 0) {
            const tempSub = savedSubjects.map(item => ({
                itemId: item._id,
                examSlug: item.exam.slug,
                subjects: []
            }));
            setCurrSubjects(tempSub);

            const max = savedSubjects[0].exam.maximumSubjects;
            setMaxSubjects(max);

            const temp = savedSubjects.flatMap(item => item.subjectsProcessed.map(sub => sub));
            setProceedSubjects(temp);

            const temp1 = savedSubjects.flatMap(item => item.subjects.map(sub => sub.subject._id));
            setRegisteredSubject(temp1);

            const update = savedSubjects[0].subjectsUpdate || [];
            setUpdateRequest(update);
        }
    }, [savedSubjects]);

    useEffect(() => {        
        if (userSubscription !== null && userSubscription !== undefined) {
            if (userSubscription || userSubscription?._id) {
                localStorage.setItem("userSubscription", userSubscription._id);
            } else {
                confirmAlert({
                    title: 'Error',
                    message: <div><p>Subscription id {userSubscription?._id} missing.</p></div>,
                    buttons: [{ label: 'OK', onClick: () => { } }],
                });
                return;
            }
        }
    }, [userSubscription]);

    // Store registered subject ID after successful save
    useEffect(() => {
        if (isSaved && savedRegisteredSubject) {
            const regSubId = savedRegisteredSubject?.regSubjects?._id || savedRegisteredSubject?.updatedSubject?._id;
            if (regSubId) {
                console.log('✅ Storing registered subject ID:', regSubId);
                localStorage.setItem("registeredSubjectId", regSubId);
            }
        }
    }, [isSaved, savedRegisteredSubject]);


    const handleCheck = (e, subject) => {
        const CurrentSubjectIndex = e.target.dataset.index;
        const temp = [...currSubjects];
        const updatedArr = temp.map(item => {
            if (item.itemId === CurrentSubjectIndex) {
                let checkArray = [...item.subjects, e.target.value];
                if (item.subjects.includes(e.target.value)) {
                    checkArray = checkArray.filter(sub => sub !== e.target.value);
                }
                return { ...item, subjects: checkArray };
            }
            return item;
        });
        setCurrSubjects(updatedArr);
    };

    const generateStyledSubjects = (subjectIds, examId) => {
        return savedSubjects
            .filter(element => element.exam._id === examId)
            .map(element => {
                return subjectIds.map((id, index) => {
                    const subject = element.subjects.find(c => c.subject._id === id);
                    const isMandatory = subject?.mandetory === true;
                    return (
                        <span key={id} style={{ fontWeight: isMandatory ? 'bold' : 'normal', color: isMandatory ? 'red' : 'black' }}>
                            <strong>({index + 1}) </strong>{subject ? subject.subject.title : ''}{' '}
                        </span>
                    );
                });
            });
    };


    const handleCustomize = async (item, action) => {
        if (!item) {
            return;
        }

        const universityHint = getEntityId(item?.subscriptionPlan?.university);
        const existingSubscriptionId = getEntityId(item?.subscriptionPlan);
        const isAttemptingExam = action === 'go_to_exam' || item.subjectsProcessed.length === item.exam.maximumSubjects;

        if (!isAttemptingExam) {
            persistExamContext(item, existingSubscriptionId, { universityId: universityHint });
            localStorage.removeItem('questionCount');
            router.push(`/course-subjects`);
            return;
        }

        let resolvedSubscription = item?.subscriptionPlan ?? null;
        let apiDeterminedInactive = false;

        if (userId && item?.exam?._id) {
            try {
                const response = await dispatch(
                    getUserSubscription(
                        userId,
                        item.exam._id,
                        universityHint
                    )
                );

                if (response?.userSubscription) {
                    resolvedSubscription = response.userSubscription;
                } else if (response && response.success === false && !response?.isError) {
                    apiDeterminedInactive = true;
                } else if (response?.isError) {
                    console.error('[SavedExamSubjects] Subscription check failed:', response.error);
                }
            } catch (err) {
                console.error('[SavedExamSubjects] Failed to refresh subscription', err);
                // Fall back to existing subscription details when API fails
            }
        }

        const resolvedSubscriptionId = getEntityId(resolvedSubscription);
        const resolvedUniversityId = getEntityId(resolvedSubscription?.university) || universityHint;

        const hasActiveSubscription = apiDeterminedInactive ? false : isSubscriptionActive(resolvedSubscription);

        if (!hasActiveSubscription) {
            persistExamContext(item, null, { universityId: resolvedUniversityId });
            localStorage.removeItem('questionCount');
            promptSubscriptionRenewal(item, item?.subscriptionPlan);
            return;
        }

        persistExamContext(item, resolvedSubscriptionId, { universityId: resolvedUniversityId });

        if ((resolvedSubscription?.planName || '').toLowerCase() === 'trial' && resolvedSubscription?.numOfQuestions) {
            localStorage.setItem('questionCount', resolvedSubscription.numOfQuestions);
        } else {
            localStorage.removeItem('questionCount');
        }

        history.push(`/exam-type`);
    };

    const handleSubmit = (e, item) => {
        e.preventDefault();
        // Try to hydrate/refresh user's subscription for this exam
        dispatch(getUserSubscription(userId, item.exam._id, item?.subscriptionPlan?.university?._id));
        const CurrentSubjectIndex = e.target.dataset.id;
        const CurrentSubj = currSubjects.find(item => item.itemId === CurrentSubjectIndex);

        if (CurrentSubj && CurrentSubj.subjects.length > 0) {
            const selectedSubjectsInfo = generateStyledSubjects(CurrentSubj?.subjects, item.exam._id);
            confirmAlert({
                customUI: ({ onClose }) => {
                    const handleYes = async () => {
                        onClose();

                        const selectedSubjects = CurrentSubj?.subjects || [];
                        const existingProcessed = Array.isArray(item?.subjectsProcessed) ? item.subjectsProcessed : [];
                        const mergedUniqueSubjects = Array.from(new Set([...existingProcessed, ...selectedSubjects]));

                        const resolvedSubscriptionId =
                            item?.subscriptionPlan?._id ||
                            userSubscription?._id ||
                            localStorage.getItem('userSubscription');

                        if (!user?._id || !item?.exam?._id || !resolvedSubscriptionId) {
                            alert.error('Missing required information. Please ensure you have an active subscription.');
                            return;
                        }

                        const payload = {
                            userId: user._id,
                            examId: item.exam._id,
                            categoryId: item?.category?._id,
                            courseId: item?.course?._id,
                            userSubscription: resolvedSubscriptionId,
                            subjects: [],
                            subjectsProcessed: mergedUniqueSubjects,
                            subjectsUpdate: [],
                            updateRequest: false
                        };

                        try {
                            await dispatch(saveExamSubjects(payload));
                        } catch (err) {
                            const backendMessage = err?.message || err?.errMessage || 'This subscription has already been used. Please purchase a new subscription.';

                            confirmAlert({
                                title: 'Subscription Required',
                                message: (
                                    <div>
                                        <p>{backendMessage}</p>
                                        <p>Would you like to purchase another subscription now?</p>
                                    </div>
                                ),
                                buttons: [
                                    {
                                        label: 'Purchase Plan',
                                        onClick: () => history.push('/subscription-plans')
                                    },
                                    {
                                        label: 'Cancel',
                                        onClick: () => { }
                                    }
                                ]
                            });

                            localStorage.removeItem('userSubscription');
                            localStorage.setItem('pendingSubjectRegistration', '/course-subjects');
                            localStorage.setItem('forceSubscriptionRenewal', 'true');
                            history.replace('/subscription-plans');
                            return;
                        }

                        setProceedSubjects(mergedUniqueSubjects);

                        await dispatch(saveSelectedSubjects(
                            user._id,
                            item.exam._id,
                            item.exam.examType,
                            item?.category?._id,
                            item?.course?._id,
                            null,
                            mergedUniqueSubjects,
                            null
                        ));

                        if ((item?.subscriptionPlan?.planName || '').toLowerCase() === 'trial') {
                            localStorage.setItem("questionCount", item?.subscriptionPlan?.numOfQuestions);
                        } else {
                            localStorage.removeItem("questionCount");
                        }

                        localStorage.setItem("maximumSubjects", item?.exam?.maximumSubjects);
                        localStorage.setItem("examId", item?.exam?._id);
                        localStorage.setItem("examSlug", item?.exam?.slug);
                        localStorage.setItem("examName", item?.exam?.title);
                        localStorage.setItem("examinationType", item?.exam?.examType);
                        localStorage.setItem("categoryId", item?.category?._id);
                        localStorage.setItem("courseId", item?.course?._id);

                        if (resolvedSubscriptionId) {
                            localStorage.setItem("userSubscription", resolvedSubscriptionId);
                        }

                        localStorage.setItem("subjects", mergedUniqueSubjects);
                        history.push(`/mode-of-assessment`);
                    };
                    
                    return (
                        <div className="react-confirm-alert-body">
                            <h1>Confirmation</h1>
                            <div>
                                <p>Are you sure you want to register these <strong className="highlighted" style={{ fontWeight: 'bold', backgroundColor: 'yellow' }}>{selectedSubjectsInfo}</strong> subjects?</p>
                                <p>Click "Yes" to confirm.</p>
                                <p className="bg-danger" style={{ color: 'red' }}>Note: You cannot change the selected subject after proceed.</p>
                            </div>
                            <div className="react-confirm-alert-button-group">
                                <button onClick={handleYes}>Yes</button>
                                <button onClick={onClose}>No</button>
                            </div>
                        </div>
                    );
                },
            });
        } else if (registeredSubject.length === 0) {
            confirmAlert({
                title: 'Error',
                message: <div><p>Please select at least one subject to proceed.</p></div>,
                buttons: [{ label: 'OK', onClick: () => { } }],
            });
        }
    };

    const tableHeader = [
        { title: '#', prop: 'id', sortable: true, filterable: true },
        { title: 'Exam', prop: 'exam', sortable: true, filterable: true },
        { title: 'Category', prop: 'category', sortable: true, filterable: true },
        { title: 'University', prop: 'university', sortable: true, filterable: true },
        { title: 'Course', prop: 'course', sortable: true, filterable: true },
        { title: 'Subjects', prop: 'subjects', sortable: false, filterable: false },
        { title: 'Date', prop: 'date', sortable: true, filterable: true },
        { title: 'Action', prop: 'action' }
    ];


    const startPendingSubscription = (subscription) => {
        if (!subscription) {
            alert.error("Subscription details are missing. Please refresh and try again.");
            return;
        }

        const exam = subscription?.exam;
        const examId =
            typeof exam === "string"
                ? exam
                : exam?._id || exam?.id || getEntityId(exam);

        if (!examId) {
            alert.error("Exam details are missing for this subscription. Please contact support.");
            return;
        }

        const examSlug = typeof exam === "object" ? exam.slug || "" : "";
        const examTitle = typeof exam === "object" ? exam.title || "" : "";
        const examType = typeof exam === "object" ? exam.examType || "" : "";
        const maximumSubjects = typeof exam === "object" ? exam.maximumSubjects || "" : "";

        localStorage.setItem("examId", examId);
        localStorage.setItem("examSlug", examSlug);
        localStorage.setItem("examName", examTitle);
        localStorage.setItem("examinationType", examType);
        localStorage.setItem("examType", examType);
        localStorage.setItem("maximumSubjects", maximumSubjects);
        localStorage.setItem("userSubscription", subscription._id);
        localStorage.removeItem("pendingSubjectRegistration");
        localStorage.removeItem("forceSubscriptionRenewal");

        const universityId = getEntityId(subscription?.university);
        if (universityId) {
            localStorage.setItem("universityId", universityId);
        } else {
            localStorage.removeItem("universityId");
        }

        // Clear stale course selection so registration starts cleanly
        localStorage.removeItem("categoryId");
        localStorage.removeItem("courseId");
        localStorage.removeItem("subjects");
        localStorage.removeItem("RegSubjects");
        localStorage.removeItem("paymentCompleted");

        const slug = String(examSlug || "").toLowerCase();
        const title = String(examTitle || "").toLowerCase();

        if (slug.includes("post-utme") || title.includes("post-utme") || title.includes("post utme")) {
            history.push("/university");
            return;
        }

        if (slug.includes("bece") || title.includes("bece") || title.includes("jsce")) {
            history.push("/course-subjects");
            return;
        }

        history.push("/categories");
    };

    const savedExamIds = new Set(
        (savedSubjects || []).map((item) => String(item?.exam?._id || "")).filter(Boolean)
    );

    const pendingSubscriptions = (Array.isArray(userSubscriptionList) ? userSubscriptionList : [])
        .filter((subscription) => {
            const examId = subscription?.exam?._id;
            if (!examId || !isSubscriptionCurrentlyActive(subscription)) {
                return false;
            }
            return !savedExamIds.has(String(examId));
        });

    const filteredSavedSubjects = (savedSubjects || []).filter((item) =>
        matchesSearchQuery(searchQuery, [
            item?.exam?.title,
            item?.category?.title,
            item?.subscriptionPlan?.university?.title,
            item?.course?.title,
            ...(item?.subjects || []).map((subject) => subject?.subject?.title),
        ])
    );

    const filteredPendingSubscriptions = pendingSubscriptions.filter((subscription) =>
        matchesSearchQuery(searchQuery, [
            subscription?.exam?.title,
            subscription?.university?.title,
            subscription?.planName,
        ])
    );

    const pageStats = useMemo(() => {
        const registrations = Array.isArray(savedSubjects) ? savedSubjects : [];
        const totalRegistrations = registrations.length + pendingSubscriptions.length;
        let jambCount = 0;
        let wassceCount = 0;
        let subjectTotal = 0;

        registrations.forEach((item) => {
            const bucket = getExamBucket(item?.exam?.slug, item?.exam?.title);
            if (bucket === 'jamb') jambCount += 1;
            if (bucket === 'wassce') wassceCount += 1;
            subjectTotal += Array.isArray(item?.subjects) ? item.subjects.length : 0;
        });

        pendingSubscriptions.forEach((subscription) => {
            const bucket = getExamBucket(subscription?.exam?.slug, subscription?.exam?.title);
            if (bucket === 'jamb') jambCount += 1;
            if (bucket === 'wassce') wassceCount += 1;
        });

        const averageSubjects = registrations.length
            ? Math.round(subjectTotal / registrations.length)
            : 0;

        return {
            totalRegistrations,
            jambCount,
            wassceCount,
            averageSubjects,
        };
    }, [savedSubjects, pendingSubscriptions]);

    const renderExamCell = (title, slug) => (
        <span className="student-registered-subjects__exam">
            <span
                className={`student-registered-subjects__exam-dot student-registered-subjects__exam-dot--${getExamTone(slug, title)}`}
            />
            {title || '—'}
        </span>
    );

    const renderCategoryBadge = (title) => {
        const tone = getCategoryTone(title);
        return (
            <span className={`student-registered-subjects__badge student-registered-subjects__badge--${tone}`}>
                {title || '—'}
            </span>
        );
    };

    const renderRegistrationActions = (item, key) => {
        const isFullyRegistered = item.subjectsProcessed.length === item.exam.maximumSubjects;

        return (
            <div className="student-registered-subjects__actions">
                <button
                    type="button"
                    className="student-registered-subjects__btn student-registered-subjects__btn--navy"
                    onClick={(e) => handleSubmit(e, item)}
                    data-id={item._id}
                    data-subjects={item.subjects}
                    data-idx={key}
                    data-exam={item.exam.slug}
                    disabled={isFullyRegistered}
                >
                    {isFullyRegistered ? (
                        <>
                            <i className="fas fa-check" aria-hidden="true" />
                            Subjects Registered
                        </>
                    ) : (
                        'Register Selected'
                    )}
                </button>
                <button
                    type="button"
                    className="student-registered-subjects__btn student-registered-subjects__btn--orange"
                    onClick={() =>
                        handleCustomize(
                            item,
                            isFullyRegistered || item.exam.slug === 'post-utme' ? 'go_to_exam' : undefined
                        )
                    }
                >
                    <i className="fas fa-external-link-alt" aria-hidden="true" />
                    Go To Exam
                </button>
                <Link
                    to="/saved-subjects-update"
                    className={`student-registered-subjects__btn student-registered-subjects__btn--orange${
                        item.updateRequest || !isFullyRegistered ? ' is-disabled' : ''
                    }`}
                    onClick={(event) => {
                        if (item.updateRequest || !isFullyRegistered) {
                            event.preventDefault();
                            return;
                        }
                        localStorage.setItem("maximumSubjects", item.exam.maximumSubjects);
                        localStorage.setItem("courseId", item.course._id);
                        localStorage.setItem("examId", item.exam._id);
                        localStorage.setItem("examSlug", item.exam.slug);
                        localStorage.setItem("examName", item.exam.title);
                        localStorage.setItem("examinationType", item.exam.examType);
                        localStorage.setItem("categoryId", item.category._id);
                        localStorage.setItem("userSubscription", item.subscriptionPlan._id);
                    }}
                    aria-disabled={item.updateRequest || !isFullyRegistered}
                >
                    <i className="fas fa-sync-alt" aria-hidden="true" />
                    {item.updateRequest ? 'Change Requested' : 'Request Change'}
                </Link>
            </div>
        );
    };

    const pendingSubscriptionRows = filteredPendingSubscriptions.map((subscription, index) => ({
        id: (filteredSavedSubjects.length || 0) + index + 1,
        exam: renderExamCell(subscription?.exam?.title, subscription?.exam?.slug),
        category: <span className="student-registered-subjects__muted">—</span>,
        university: subscription?.university?.title || '—',
        course: <span className="student-registered-subjects__muted">—</span>,
        subjects: (
            <div className="student-registered-subjects__pending-block">
                <p className="student-registered-subjects__pending-note">
                    Subscription active — subjects not registered yet
                </p>
                <p className="student-registered-subjects__pending-hint">
                    Choose your course and subjects to finish setup
                </p>
            </div>
        ),
        date: subscription?.subscribedAt
            ? moment(subscription.subscribedAt).format('DD MMM YYYY')
            : '—',
        action: (
            <div className="student-registered-subjects__actions">
                <button
                    type="button"
                    className="student-registered-subjects__btn student-registered-subjects__btn--navy"
                    disabled
                >
                    Subjects Pending
                </button>
                <button
                    type="button"
                    className="student-registered-subjects__btn student-registered-subjects__btn--orange"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        startPendingSubscription(subscription);
                    }}
                >
                    <i className="fas fa-arrow-right" aria-hidden="true" />
                    Set Up Subjects
                </button>
            </div>
        ),
    }));

    const tableRowData = [
        ...filteredSavedSubjects.map((item, key) => ({
            id: key + 1,
            exam: renderExamCell(item?.exam?.title, item?.exam?.slug),
            category: renderCategoryBadge(item?.category?.title),
            university: item?.subscriptionPlan?.university?.title || '—',
            course: item?.course?.title || '—',
            subjects: (
                <SubjectListCell
                    item={item}
                    rowKey={key}
                    currSubjects={currSubjects}
                    onCheck={(e) => handleCheck(e, null)}
                />
            ),
            date: moment(item.createdAt).format('DD MMM YYYY'),
            action: renderRegistrationActions(item, key),
        })),
        ...pendingSubscriptionRows,
    ];

    const onSortFunction = {
        date(columnValue) {
            return moment(columnValue, 'DD MMM YYYY').valueOf();
        }
    };

    const customLabels = {
        first: '<<',
        last: '>>',
        prev: '<',
        next: '>',
        show: 'Display',
        entries: 'rows',
        noResults: 'There are no data to be displayed'
    };

    const registrationCount = tableRowData.length;

    return (
        <Fragment>
            {csloading ? (
                <Loader />
            ) : (
                <div className="student-registered-subjects">
                    <div className="student-registered-subjects__header">
                        <h1>Registered Subjects</h1>
                        <p>All your saved exam subject registrations</p>
                    </div>

                    {filteredPendingSubscriptions.length > 0 ? (
                        <div className="student-registered-subjects__notice" role="status">
                            <div>
                                <p className="student-registered-subjects__notice-title">
                                    Finish subject registration
                                </p>
                                <p className="student-registered-subjects__notice-text">
                                    You have an active subscription without registered subjects.
                                    Complete setup to match your exam registrations.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="student-registered-subjects__btn student-registered-subjects__btn--orange"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    startPendingSubscription(filteredPendingSubscriptions[0]);
                                }}
                            >
                                <i className="fas fa-arrow-right" aria-hidden="true" />
                                Continue Setup
                            </button>
                        </div>
                    ) : null}

                    <div className="student-registered-subjects__stats">
                        <div className="student-registered-subjects__stat">
                            <span className="student-registered-subjects__stat-icon student-registered-subjects__stat-icon--blue">
                                <i className="fas fa-book-open" aria-hidden="true" />
                            </span>
                            <div>
                                <p className="student-registered-subjects__stat-label">Total Registrations</p>
                                <p className="student-registered-subjects__stat-value">{pageStats.totalRegistrations}</p>
                                <p className="student-registered-subjects__stat-subtext">across all exams</p>
                            </div>
                        </div>
                        <div className="student-registered-subjects__stat">
                            <span className="student-registered-subjects__stat-icon student-registered-subjects__stat-icon--green">
                                <i className="fas fa-flask" aria-hidden="true" />
                            </span>
                            <div>
                                <p className="student-registered-subjects__stat-label">JAMB UTME</p>
                                <p className="student-registered-subjects__stat-value">{pageStats.jambCount}</p>
                                <p className="student-registered-subjects__stat-subtext">registrations</p>
                            </div>
                        </div>
                        <div className="student-registered-subjects__stat">
                            <span className="student-registered-subjects__stat-icon student-registered-subjects__stat-icon--purple">
                                <i className="fas fa-shield-alt" aria-hidden="true" />
                            </span>
                            <div>
                                <p className="student-registered-subjects__stat-label">WASSCE</p>
                                <p className="student-registered-subjects__stat-value">{pageStats.wassceCount}</p>
                                <p className="student-registered-subjects__stat-subtext">registrations</p>
                            </div>
                        </div>
                        <div className="student-registered-subjects__stat">
                            <span className="student-registered-subjects__stat-icon student-registered-subjects__stat-icon--orange">
                                <i className="fas fa-book" aria-hidden="true" />
                            </span>
                            <div>
                                <p className="student-registered-subjects__stat-label">Avg. Subjects</p>
                                <p className="student-registered-subjects__stat-value">{pageStats.averageSubjects}</p>
                                <p className="student-registered-subjects__stat-subtext">per registration</p>
                            </div>
                        </div>
                    </div>

                    <div className="student-registered-subjects__table-card">
                        <div className="student-registered-subjects__table-head">
                            <div>
                                <h2>Saved Exam Subjects</h2>
                                <p>{registrationCount} registration{registrationCount === 1 ? '' : 's'} found</p>
                            </div>
                            <div className="student-registered-subjects__search-wrap">
                                <i className="fas fa-search student-registered-subjects__search-icon" aria-hidden="true" />
                                <input
                                    type="search"
                                    className="student-registered-subjects__search"
                                    placeholder="Search exam, course, subject..."
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                />
                            </div>
                        </div>

                        <CustomDataTable
                            tableHeaders={tableHeader}
                            tableBody={tableRowData}
                            rowsPerPage={10}
                            rowsPerPageOption={[10, 20, 30, 40]}
                            initialSort={{ prop: 'date', isAscending: false }}
                            onSort={onSortFunction}
                            labels={customLabels}
                            filterable={false}
                            tableClass="table student-registered-subjects__table"
                            disablePersistence
                        />
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default SavedExamSubjects;
